import API from 'utils/api';
import { onError } from 'utils/handlers';
import { ESTATUS_AUTORIZADO } from 'utils/constants';
import {
  ESTADO_SEGUIMIENTO,
  evaluarSeguimiento,
  huellaRegistro,
} from 'api/nominas/seguimientoProceso';

/**
 * Capa de timbrado de nómina: única puerta del frontend hacia el servicio de timbrado.
 * Las vistas no conocen URLs, códigos HTTP, descripciones de procesos ni la forma
 * de las respuestas del backend. Ninguna función lanza; los errores van a onError.
 */

// Solo se timbran nóminas autorizadas. Va al final de los params para que la
// búsqueda de la tabla no pueda sobrescribirlo.
const FILTRO_NOMINAS_TIMBRABLES = { estados_globales: ESTATUS_AUTORIZADO };

// Descripciones registradas por las tasks Celery en ProcesoEnCursoTerminado (backend)
const DESCRIPCION_PROCESO_TIMBRADO = 'Timbrar Nomina';
const DESCRIPCION_PROCESO_CONSULTA = 'Consultar Timbrado Nomina';
const DESCRIPCION_PROCESO_CANCELACION = 'Cancelar Timbrado Nomina';

const SIN_RESULTADO = {
  iniciado: false, procesoEnCurso: false, mensaje: null, proceso: null,
};

// La fila de progreso es global por descripción (no distingue nómina, usuario ni
// entidad): si ya está procesando, lanzar otra tarea la duplicaría en la cola.
const MENSAJE_PROCESO_EN_CURSO = 'Ya existe un proceso de este tipo en curso (puede ser de otra '
  + 'nómina). Espere a que termine antes de lanzarlo de nuevo.';

// El backend responde 409 cuando la nómina ya tiene un proceso pendiente o en
// ejecución (p. ej. Timbrar Todo con la tarea aún en cola). No es un error: no se
// lanzó nada y no hay corrida nueva que seguir.
const MENSAJE_CONFLICTO_POR_DEFECTO = 'Ya hay un proceso pendiente o en ejecución para esta '
  + 'nómina. Espere a que termine antes de volver a lanzarlo.';

// Consultas de progreso (una cada 2 s, ≈ 11 min) antes de dejar de dar seguimiento.
// Es también el tiempo de espera para confirmar el inicio: la tarea va a la cola
// por defecto de Celery, compartida con otros procesos, y puede tardar en tomarse.
export const MAX_CONSULTAS_SEGUIMIENTO = 340;

/**
 * Lee la fila de progreso de un proceso sin avisar al usuario si falla: quien la
 * llama decide qué hacer (la consulta compartida de procesos en curso mostraría
 * un aviso en cada falla del seguimiento).
 * @param {string} descripcionProceso
 * @returns {Promise<{ok: boolean, registro: object|null}>}
 */
const leerFilaProceso = async (descripcionProceso) => {
  try {
    const response = await API.get('/catalogos/proceso-terminado/', {
      params: { descripcion: descripcionProceso },
    });
    const { data } = response;
    const lista = Array.isArray(data) ? data : (data?.results || []);
    return { ok: true, registro: lista[0] || null };
  } catch (err) {
    return { ok: false, registro: null };
  }
};

/**
 * Lee la fila de progreso del proceso antes de lanzarlo, para no confundirla
 * después con la de la corrida nueva. Si falla, el seguimiento solo acepta el
 * término tras ver el proceso en curso.
 * @param {string} descripcionProceso
 * @returns {Promise<{leido: boolean, huella: string|null, procesando: boolean}>}
 */
const leerEstadoPrevio = async (descripcionProceso) => {
  const { ok, registro } = await leerFilaProceso(descripcionProceso);
  return ok
    ? { leido: true, huella: huellaRegistro(registro), procesando: registro?.procesando === true }
    : { leido: false, huella: null, procesando: false };
};

/**
 * Capacidades que el backend de timbrado soporta hoy. No son datos simulados:
 * es el único interruptor que las vistas consultan para habilitar una acción.
 * - cancelacionIndividual: el backend aún responde que la cancelación de un
 *   recibo individual no está disponible.
 */
export const CAPACIDADES = Object.freeze({ cancelacionIndividual: false });

/**
 * Interpreta la respuesta de una acción de timbrado sobre la nómina completa.
 * El backend responde 202 cuando lanzó el proceso en background (hay que dar
 * seguimiento); 200 significa que no había nada que procesar y solo regresa un
 * mensaje informativo.
 * @param {object|false} response
 * @param {string} descripcionProceso
 * @param {{leido: boolean, huella: string|null}} previo fila de progreso antes de lanzar
 * @returns {{iniciado: boolean, procesoEnCurso: boolean, mensaje: string|null,
 *   proceso: object|null}}
 */
const interpretarInicio = (response, descripcionProceso, previo) => {
  if (response?.status === 202) {
    return {
      iniciado: true,
      procesoEnCurso: false,
      mensaje: null,
      proceso: { descripcion: descripcionProceso, previo },
    };
  }
  if (response?.data) {
    return {
      ...SIN_RESULTADO,
      mensaje: typeof response.data === 'string'
        ? response.data
        : 'No fue necesario iniciar el proceso.',
    };
  }
  return SIN_RESULTADO;
};

/**
 * Lanza un proceso de timbrado sobre la nómina completa, salvo que la fila de
 * progreso ya esté procesando. Si la lectura previa falla se lanza igual: el
 * seguimiento solo acepta el término tras ver el proceso en curso. Si el backend
 * rechaza el lanzamiento con 409 (proceso pendiente o en ejecución para la nómina),
 * regresa `procesoEnCurso` con su mensaje, sin aviso de error ni seguimiento.
 * @param {string} descripcionProceso
 * @param {() => Promise<object>} lanzar petición que inicia el proceso
 * @returns {Promise<{iniciado: boolean, procesoEnCurso: boolean, mensaje: string|null,
 *   proceso: object|null}>}
 */
const lanzarProceso = async (descripcionProceso, lanzar) => {
  try {
    const previo = await leerEstadoPrevio(descripcionProceso);
    if (previo.procesando) {
      return { ...SIN_RESULTADO, procesoEnCurso: true, mensaje: MENSAJE_PROCESO_EN_CURSO };
    }
    const response = await lanzar();
    return interpretarInicio(response, descripcionProceso, previo);
  } catch (err) {
    if (err?.response?.status === 409) {
      const detalle = err.response.data?.detail;
      return {
        ...SIN_RESULTADO,
        procesoEnCurso: true,
        mensaje: typeof detalle === 'string' && detalle ? detalle : MENSAJE_CONFLICTO_POR_DEFECTO,
      };
    }
    onError(err);
    return SIN_RESULTADO;
  }
};

/**
 * GET Catálogo de estatus de timbrado
 * @returns {Promise<object[]>} siempre un arreglo ([] si falla)
 */
export const getEstatusTimbrado = async () => {
  try {
    const response = await API.get('nominas/tipos-de-estatus-timbrado');
    return Array.isArray(response.data) ? response.data : [];
  } catch (err) {
    onError(err);
    return [];
  }
};

/**
 * GET Nóminas que se pueden timbrar (solo autorizadas), paginadas
 * @param {number} page
 * @param {object|null} q filtros de búsqueda de la tabla
 * @returns {Promise<object|null>} respuesta paginada, null si falla
 */
export const getNominasTimbrables = async (page = 1, q = null) => {
  try {
    const response = await API.get('nominas/nominas', {
      params: { page, ...q, ...FILTRO_NOMINAS_TIMBRABLES },
    });
    return response.data;
  } catch (err) {
    onError(err);
    return null;
  }
};

/**
 * GET Recibos (NominaPlaza) de una nómina, paginados, con búsqueda y filtro por estatus
 * (ticket 3). `nomina` va al final de los params para que ninguna búsqueda ni filtro
 * cambie la nómina abierta.
 * @param {number} nominaId
 * @param {number} page
 * @param {object|null} q búsqueda de la tabla: filtros de texto de NominaPlazaFilterSet
 *   (`plaza_no`, `empleado_numero`, `empleado_numero_y_nombre_completo`,
 *   `mensaje_estatus_timbrado`, ...)
 * @param {number|null} estatus id del catálogo de estatus de timbrado (filtro exacto)
 * @returns {Promise<object|null>} respuesta paginada, null si falla
 */
export const getRecibosDeNomina = async (nominaId, page = 1, q = null, estatus = null) => {
  try {
    const response = await API.get('nominas/nominas-plazas', {
      params: {
        ...q,
        ...(estatus ? { tipo_de_estatus_timbrado: estatus } : {}),
        page,
        nomina: nominaId,
      },
    });
    return response.data;
  } catch (err) {
    onError(err);
    return null;
  }
};

/**
 * GET Datos del timbrado de un recibo (NominaPlaza).
 * Un recibo puede tener varios registros de timbrado; se regresa el más reciente
 * (mayor id) y el total de registros. No expone campos internos del servicio
 * (uuid_peticion, ruta_xml). `xml` se incluye para la descarga/vista del XML.
 * Siempre se pide filtrado por nomina_plaza: sin ese filtro el backend regresaría
 * los timbrados (con XML) de toda la entidad.
 *
 * Dependencia de backend por confirmar: hoy `xml` solo lo escribe el callback del
 * PAC (`timbrados-nomina-actualizar-timbrado`), y ningún flujo de SAC revisado
 * escribe `fecha_de_timbrado` ni `importe`. Sin ese callback el panel muestra '—'
 * en Fecha e Importe y el XML queda deshabilitado; no es un error del frontend.
 * @param {number} nominaPlazaId
 * @returns {Promise<{registro: object|null, total: number, error: boolean}>}
 */
export const getDatosTimbradoRecibo = async (nominaPlazaId) => {
  if (!nominaPlazaId) return { registro: null, total: 0, error: false };
  try {
    // Sin `page` el backend responde sin paginar (todos los registros del recibo)
    const response = await API.get('nominas/nominas-plazas-timbrados', {
      params: { nomina_plaza: nominaPlazaId },
    });
    const lista = Array.isArray(response.data) ? response.data : response.data?.results;
    const registros = (Array.isArray(lista) ? lista : [])
      .filter((r) => Number(r?.nomina_plaza) === Number(nominaPlazaId));
    const ultimo = registros.reduce((max, r) => (!max || r.id > max.id ? r : max), null);
    return {
      registro: ultimo && {
        uuid: ultimo.uuid_timbrado || null,
        fechaTimbrado: ultimo.fecha_de_timbrado || null,
        rfcReceptor: ultimo.rfc_receptor || null,
        importe: ultimo.importe ?? null,
        mensaje: ultimo.mensaje_estatus_timbrado || null,
        estatusId: ultimo.tipo_de_estatus_timbrado ?? null,
        xml: ultimo.xml || null,
      },
      total: registros.length,
      error: false,
    };
  } catch (err) {
    onError(err);
    return { registro: null, total: 0, error: true };
  }
};

/**
 * PUT Timbrado de todos los recibos de la nómina
 * @param {number} nominaId
 * @returns {Promise<{iniciado: boolean, procesoEnCurso: boolean, mensaje: string|null,
 *   proceso: object|null}>}
 */
export const iniciarTimbrado = (nominaId) => lanzarProceso(
  DESCRIPCION_PROCESO_TIMBRADO,
  () => API.put(`nominas/timbrados-nomina-timbrado-completo/${nominaId}`),
);

/**
 * GET Consulta del estatus de timbrado de la nómina
 * @param {number} nominaId
 * @returns {Promise<{iniciado: boolean, procesoEnCurso: boolean, mensaje: string|null,
 *   proceso: object|null}>}
 */
export const iniciarConsulta = (nominaId) => lanzarProceso(
  DESCRIPCION_PROCESO_CONSULTA,
  () => API.get(`nominas/timbrados-nomina-consulta/${nominaId}`),
);

/**
 * POST Cancelación del timbrado de todos los recibos de la nómina
 * @param {number} nominaId
 * @returns {Promise<{iniciado: boolean, procesoEnCurso: boolean, mensaje: string|null,
 *   proceso: object|null}>}
 */
export const iniciarCancelacionNomina = (nominaId) => lanzarProceso(
  DESCRIPCION_PROCESO_CANCELACION,
  () => API.post(`nominas/timbrados-nomina-cancelacion-completa/${nominaId}`),
);

/**
 * GET Cancelación del timbrado de un recibo (NominaPlaza) individual.
 * No lanza proceso en background: solo regresa un mensaje.
 * @param {number} nominaPlazaId
 * @returns {Promise<{mensaje: string|null}>}
 */
export const solicitarCancelacionRecibo = async (nominaPlazaId) => {
  try {
    const response = await API.get(`nominas/timbrados-nominaplaza-cancelacion/${nominaPlazaId}`);
    if (response?.data) {
      return {
        mensaje: typeof response.data === 'string'
          ? response.data
          : 'Se ha solicitado la cancelación del recibo.',
      };
    }
  } catch (err) {
    onError(err);
  }
  return { mensaje: null };
};

/**
 * POST Liberación manual del bloqueo de Timbrar Todo de una nómina atascada (ticket 18).
 * El servidor rechaza (409) si no hay bloqueo o si la corrida registró actividad reciente.
 * @param {number} nominaId
 * @param {string} motivo
 * @returns {Promise<{liberado: boolean, mensaje: string|null}>}
 */
export const liberarBloqueoTimbrado = async (nominaId, motivo) => {
  try {
    const response = await API.post(`nominas/timbrados-nomina-liberar-bloqueo/${nominaId}/`, { motivo });
    return { liberado: true, mensaje: response?.data?.detail || null };
  } catch (err) {
    onError(err);
  }
  return { liberado: false, mensaje: null };
};

/**
 * GET Progreso de un proceso de timbrado lanzado con iniciar*.
 * Mientras no se confirme que la fila es de esta corrida, no regresa su avance
 * ni sus errores (serían los de la corrida anterior).
 * Si la consulta falla regresa `error: true` sin avisar al usuario; en ese caso
 * `enCurso` y `total` no son avance real y no deben mostrarse, y `seguimiento`
 * conserva lo ya confirmado.
 * @param {object} proceso referencia opaca regresada por iniciar*
 * @param {object} [opciones]
 * @param {object|null} [opciones.seguimiento] el `seguimiento` de la consulta anterior
 * @param {boolean} [opciones.vencido] si esta es la última consulta permitida
 * @returns {Promise<{error: boolean, terminado: boolean, inicioNoConfirmado: boolean,
 *   enCurso: number, total: number, errores: string[], seguimiento: object}>}
 */
export const getProgresoProceso = async (proceso, { seguimiento = null, vencido = false } = {}) => {
  const vistoEnCurso = !!seguimiento?.vistoEnCurso;
  const { ok, registro } = await leerFilaProceso(proceso?.descripcion);
  if (!ok) {
    return {
      error: true,
      terminado: false,
      inicioNoConfirmado: vencido && !vistoEnCurso,
      enCurso: 0,
      total: 0,
      errores: [],
      seguimiento: { vistoEnCurso },
    };
  }
  const estado = evaluarSeguimiento({
    previo: proceso?.previo, registro, vistoEnCurso, vencido,
  });
  const terminado = estado === ESTADO_SEGUIMIENTO.TERMINADO;
  const esDeEstaCorrida = terminado || estado === ESTADO_SEGUIMIENTO.EN_CURSO;

  let resultado = null;
  if (terminado) {
    try {
      resultado = registro.resultado ? JSON.parse(registro.resultado) : null;
    } catch (e) { /* resultado no es JSON */ }
  }

  return {
    error: false,
    terminado,
    inicioNoConfirmado: estado === ESTADO_SEGUIMIENTO.NO_CONFIRMADO,
    enCurso: esDeEstaCorrida ? registro.registro_en_curso || 0 : 0,
    total: esDeEstaCorrida ? registro.total_registros || 0 : 0,
    errores: Array.isArray(resultado?.errores) ? resultado.errores : [],
    seguimiento: { vistoEnCurso: vistoEnCurso || estado === ESTADO_SEGUIMIENTO.EN_CURSO },
  };
};

/**
 * Indica si la nómina tiene una consulta de timbrado pendiente con el servicio.
 * @param {object} nomina encabezado de la nómina
 * @returns {boolean}
 */
export const hayConsultaPendiente = (nomina) => !nomina?.nomina_termino_servicio_consulta
  && nomina?.peticion_pendiente_id != null;
